import React, {useState} from 'react';
import {BrowserRouter as Router, Link, Navigate, Route, Routes} from 'react-router-dom';
import {
    AppBar,
    Avatar,
    Box,
    Button,
    createTheme,
    CssBaseline,
    Drawer,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    SvgIcon,
    ThemeProvider,
    Toolbar,
    Typography,
    useMediaQuery,
} from '@mui/material';

import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';

import Login from "./login/Login";
import ForgotPassword from "./login/ForgotPassword";
import ResetPassword from "./login/ResetPassword";
import Register from "./register/Register";
import Checkout from "./checkout/Checkout";
import {useAuth} from './auth-context';
import PricingTable from "./checkout/PricingTable";
import Dashboard from "./dashboard/Dashboard";
import DashboardDetailPage from "./dashboard/DashboardDetailPage";
import DefendantForm from "./defendant/DefendantForm";
import DefendantProfile from "./defendant/DefendantProfile";
import DefendantsPage from "./defendant/DefendantsPage";
import PrivateRoute from "./core/PrivateRoute";
import AdminRoute from "./core/AdminRoute";
import Confirmation from "./checkout/Confirmation";
import {ClientProvider} from "./core/client";
import ActivityLog from "./activity/ActivityLog";
import Profile from "./profile/Profile";
import Account from "./account/Account";
import Impersonate from "./admin/Impersonate";
import ImpersonationBanner from "./admin/ImpersonationBanner";

const drawerWidth = 261;
const appBarHeight = 64;

const appTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: { main: '#1EB980' },
        secondary: { main: '#045D56' },
    },
    typography: {
        fontFamily: ['Roboto Condensed', 'Space Mono', 'Eczar'].join(','),
        fontSize: 14,
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    color: '#fff',
                },
            },
        },
    },
});

const App = () => {
    const { authToken, logout, user } = useAuth();
    const authenticated = !!authToken;
    const isAdmin = (user?.role || '').toLowerCase() === 'admin';

    const isMobile = useMediaQuery(appTheme.breakpoints.down('md'));

    const [anchorElUser, setAnchorElUser] = useState(null);
    const [desktopOpen, setDesktopOpen] = useState(true);
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleDrawerToggle = () => {
        if (isMobile) {
            setMobileOpen(v => !v);
        } else {
            setDesktopOpen(v => !v);
        }
    };

    const closeMobileDrawer = () => {
        if (isMobile) setMobileOpen(false);
    };

    const handleOpenUserMenu = (e) => setAnchorElUser(e.currentTarget);
    const handleCloseUserMenu = () => setAnchorElUser(null);
    const handleLogout = () => {
        logout();
        handleCloseUserMenu();
    };

    const settings = [
        { text: 'Profile', to: '/profile', method: handleCloseUserMenu },
        { text: 'Account', to: '/account', method: handleCloseUserMenu },
        { text: 'Logout', method: handleLogout },
    ];
    const profileDisplayName = [user?.first, user?.fist, user?.last]
        .filter(Boolean)
        .join(' ')
        .trim() || user?.email || 'User';

    const drawerContent = (
        <Box sx={{ width: drawerWidth }}>
            <List>
                <ListItem disablePadding>
                    <ListItemButton component={Link} to="/" onClick={closeMobileDrawer}>
                        <ListItemIcon><DashboardIcon /></ListItemIcon>
                        <ListItemText primary="Dashboard" />
                    </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                    <ListItemButton component={Link} to="/defendants" onClick={closeMobileDrawer}>
                        <ListItemIcon><PeopleIcon /></ListItemIcon>
                        <ListItemText primary="Defendants" />
                    </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                    <ListItemButton component={Link} to="/activity" onClick={closeMobileDrawer}>
                        <ListItemIcon><ListAltIcon /></ListItemIcon>
                        <ListItemText primary="Activity Log" />
                    </ListItemButton>
                </ListItem>

                {isAdmin && (
                    <ListItem disablePadding>
                        <ListItemButton component={Link} to="/admin/impersonate" onClick={closeMobileDrawer}>
                            <ListItemIcon><AccountCircleOutlinedIcon /></ListItemIcon>
                            <ListItemText primary="Admin" />
                        </ListItemButton>
                    </ListItem>
                )}
            </List>
        </Box>
    );

    const desktopShift = authenticated && !isMobile && desktopOpen ? drawerWidth : 0;

    return (
        <ThemeProvider theme={appTheme}>
            <CssBaseline />
            <ClientProvider>
                <Router>
                    <Box sx={{ display: 'flex', width: '100%', overflowX: 'hidden' }}>

                    {/* APP BAR — FULL WIDTH */}
                    <AppBar
                        position="fixed"
                        sx={{
                            zIndex: (theme) => theme.zIndex.drawer + 1,
                        }}
                    >
                        <Toolbar sx={{ minHeight: appBarHeight }}>
                            {authenticated && (
                                <IconButton
                                    color="inherit"
                                    edge="start"
                                    onClick={handleDrawerToggle}
                                    sx={{ mr: 1 }}
                                >
                                    <MenuIcon />
                                </IconButton>
                            )}

                            <SvgIcon style={{ marginRight: 10 }}>
                                <svg
                                    width="888"
                                    height="1036"
                                    viewBox="0 0 888 1036"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle cx="444" cy="524.001" r="319" stroke="#03ECBA" strokeWidth="250" />
                                    <path fillRule="evenodd" clipRule="evenodd"
                                          d="M444 718.001C336.857 718.001 250 631.144 250 524.001C250 416.858 336.857 330.001 444 330.001C551.143 330.001 638 416.858 638 524.001H888C888 278.787 689.214 80.001 444 80.001C198.786 80.001 0 278.787 0 524.001C0 769.215 198.786 968.001 444 968.001V718.001Z"
                                          fill="#027C54" />
                                    <path
                                        d="M888 160.001C888 204.184 852.183 240.001 808 240.001C763.817 240.001 728 204.184 728 160.001C728 115.818 763.817 80.001 808 80.001C852.183 80.001 888 115.818 888 160.001Z"
                                        fill="#04B682" />
                                    <path d="M444 80.001H802V233.001H444V80.001Z" fill="#04B682" />
                                    <path d="M747 168.001H888V500.001H747V168.001Z" fill="#04B682" />
                                    <line x1="887.5" y1="500.001" x2="887.5" y2="524.001" stroke="#04B682" />
                                    <path fillRule="evenodd" clipRule="evenodd"
                                          d="M444 80.001C198.786 80.001 0 278.787 0 524.001H250C250 416.858 336.857 330.001 444 330.001V80.001Z"
                                          fill="#045E57" />
                                    <path fillRule="evenodd" clipRule="evenodd"
                                          d="M888 524.001C888 278.787 689.214 80.001 444 80.001C443.333 80.001 442.666 80.0024 442 80.0054V330.011C442.666 330.004 443.333 330.001 444 330.001C551.143 330.001 638 416.858 638 524.001H888Z"
                                          fill="#04B682" />
                                </svg>
                            </SvgIcon>

                            <Box sx={{ flexGrow: 1, display: { xs: 'block', md: 'none' } }} />

                            <Typography
                                variant="h6"
                                noWrap
                                sx={{
                                    fontFamily: 'monospace',
                                    fontWeight: 700,
                                    letterSpacing: '.3rem',
                                    flexGrow: 1,
                                    display: { xs: 'none', md: 'block' },
                                }}
                            >
                                LOCATION TRACKER
                            </Typography>

                            {!authenticated && (
                                <>
                                    <Button color="inherit" component={Link} to="/login">Login</Button>
                                    <Button color="inherit" component={Link} to="/register">Register</Button>
                                </>
                            )}

                            {authenticated && (
                                <>
                                    <IconButton onClick={handleOpenUserMenu}>
                                        {user?.profilePhotoUrl ? (
                                            <Avatar
                                                src={user.profilePhotoUrl}
                                                alt={user.email || 'Profile'}
                                                sx={{ width: 32, height: 32 }}
                                            />
                                        ) : (
                                            <AccountCircleOutlinedIcon />
                                        )}
                                    </IconButton>
                                    <Menu
                                        anchorEl={anchorElUser}
                                        open={Boolean(anchorElUser)}
                                        onClose={handleCloseUserMenu}
                                        PaperProps={{
                                            sx: {
                                                minWidth: 280,
                                                p: 0,
                                                overflow: 'hidden',
                                            },
                                        }}
                                    >
                                        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar
                                                src={user?.profilePhotoUrl || ''}
                                                alt={profileDisplayName}
                                                sx={{ width: 48, height: 48 }}
                                            >
                                                {String(profileDisplayName).charAt(0).toUpperCase()}
                                            </Avatar>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                                    {profileDisplayName}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                                                >
                                                    {user?.email || ''}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Divider />
                                        {settings.map(s => (
                                            <MenuItem
                                                key={s.text}
                                                component={s.to ? Link : 'li'}
                                                to={s.to}
                                                onClick={s.method}
                                            >
                                                {s.text}
                                            </MenuItem>
                                        ))}
                                    </Menu>
                                </>
                            )}
                        </Toolbar>
                    </AppBar>

                    {/* DRAWERS — BELOW APP BAR */}
                    {authenticated && (
                        <>
                            <Drawer
                                variant="temporary"
                                open={mobileOpen}
                                onClose={handleDrawerToggle}
                                sx={{
                                    display: { xs: 'block', md: 'none' },
                                    '& .MuiDrawer-paper': {
                                        width: drawerWidth,
                                        top: appBarHeight,
                                        height: `calc(100% - ${appBarHeight}px)`,
                                        overflowX: 'hidden',
                                    },
                                }}
                            >
                                {drawerContent}
                            </Drawer>

                            <Drawer
                                variant="persistent"
                                open={desktopOpen}
                                sx={{
                                    display: { xs: 'none', md: 'block' },
                                    '& .MuiDrawer-paper': {
                                        width: drawerWidth,
                                        top: appBarHeight,
                                        height: `calc(100% - ${appBarHeight}px)`,
                                        overflowX: 'hidden',
                                    },
                                }}
                            >
                                {drawerContent}
                            </Drawer>
                        </>
                    )}

                    {/* MAIN CONTENT */}
                    <Box
                        component="main"
                        sx={{
                            flexGrow: 1,
                            mt: `${appBarHeight}px`,
                            ml: { md: `${desktopShift}px` },
                            width: { xs: '100%', md: `calc(100% - ${desktopShift}px)` },
                            p: { xs: 1.5, sm: 2, md: 3 },
                            transition: (t) =>
                                t.transitions.create(['margin-left', 'width'], {
                                    easing: t.transitions.easing.sharp,
                                    duration: t.transitions.duration.standard,
                                }),
                        }}
                    >
                        <ImpersonationBanner />

                        <Routes>
                            <Route path="/login" element={<Login />} />
                            <Route path="/forgot-password" element={<ForgotPassword />} />
                            <Route path="/reset-password" element={<ResetPassword />} />
                            <Route path="/register" element={<Register />} />
                            <Route path="/pricing" element={<PricingTable />} />
                            <Route path="/confirmation/:sessionId" element={<Confirmation />} />

                            <Route element={<PrivateRoute />}>
                                <Route path="/" element={<Dashboard />} />
                                <Route path="/dashboard/total-defendants" element={<DashboardDetailPage metric="total" />} />
                                <Route path="/dashboard/missed-checkins" element={<DashboardDetailPage metric="missed" />} />
                                <Route path="/dashboard/completed-today" element={<DashboardDetailPage metric="completed" />} />
                                <Route path="/defendants" element={<DefendantsPage />} />
                                <Route path="/defendant" element={<DefendantForm />} />
                                <Route path="/checkout/:sessionId" element={<Checkout />} />
                                <Route path="/defendant/:id" element={<DefendantProfile />} />
                                <Route path="/activity" element={<ActivityLog />} />
                                <Route path="/profile" element={<Profile />} />
                                <Route path="/account" element={<Account />} />
                            </Route>

                            <Route element={<AdminRoute />}>
                                <Route path="/admin/impersonate" element={<Impersonate />} />
                            </Route>

                            <Route path="*" element={<Navigate to={authenticated ? "/" : "/login"} replace />} />
                        </Routes>
                    </Box>

                </Box>
                </Router>
            </ClientProvider>
        </ThemeProvider>
    );
};

export default App;
